// DEPENDENCIES ---------------------------------------------------------------- //

import { getMonetaryValue } from '@services/formatService';

import { css } from '@emotion/css';
import { Theme, Typography, useTheme } from '@mui/material';

import Spacer from '@components/Utils/Spacer';

import {
  RECOUP_LABEL,
  YOUR_SHARE_LABEL,
  summarizeProgramCoverage,
  type ProgramCoverageLines,
} from '@features/moveCreation/lib/programCoverageLines';
import type { EligibilityView } from '@features/moveCreation/lib/programVehicleEligibility';
import { EligibilityRuleRows } from '@hopdrive/feature-program-claims';

// COMPONENTS ---------------------------------------------------------------- //

/**
 * The money the pinned coverage bar and the summary above Submit Plan both
 * show. A dealer reads one box at the top of the form and the other at the
 * bottom, so they render through the same parts and can't name different
 * figures. Each caller decides when money may show at all (never before the
 * vehicle is in, since the VIN decode can still change it).
 */

/** The collapsed bar's figure: the label, then the amount with the asterisk the estimate note explains. */
export const CoverageHeadline = ({ lines }: { lines: ProgramCoverageLines }) => {
  const theme = useTheme();
  const summary = summarizeProgramCoverage(lines);
  return (
    <>
      <Typography sx={{ fontSize: `13px`, color: theme?.palette?.text?.secondary, whiteSpace: 'nowrap' }}>
        {summary.label}
      </Typography>
      <Typography sx={{ fontSize: `14px`, fontWeight: 700, whiteSpace: 'nowrap' }}>
        {getMonetaryValue(summary.value)}
        <span style={{ color: theme?.palette?.text?.secondary }}> *</span>
      </Typography>
    </>
  );
};

interface CoverageDetailsProps {
  /** The money breakdown, or null while it must not show (the vehicle isn't in yet). */
  lines: ProgramCoverageLines | null;
  /** The rule rows, or null while a quote is in flight (they'd judge the previous entry). */
  eligibility: EligibilityView | null;
  vehicleLabel: string | null;
  distanceMiles: number | null;
}

/** The expanded body: the estimate note and how the move cost splits, then every eligibility rule. */
export const CoverageDetails = ({ lines, eligibility, vehicleLabel, distanceMiles }: CoverageDetailsProps) => {
  const theme = useTheme();
  const cls = useStyles();
  const sx = useSxStyles(theme);

  // Without money above them the rows need no heading; the box title says what they are.
  if (!lines) return eligibility ? <EligibilityRuleRows view={eligibility} /> : null;

  return (
    <>
      <Typography variant='body2' sx={{ lineHeight: 1, fontSize: `12px`, color: theme?.palette?.text?.secondary }}>
        * Estimated{vehicleLabel ? ` for a ${vehicleLabel}` : ''}. The program decides final coverage after it
        reviews the move's data.
      </Typography>

      <Spacer />

      {/* Past the program's covered bands the dealer's share grows with the
          miles, so the distance is what explains a bigger figure. */}
      {distanceMiles != null ? (
        <div className={cls.flex}>
          <Typography sx={sx.keyText}>Distance</Typography>
          <Typography sx={sx.valText}>{distanceMiles.toFixed(1)} mi</Typography>
        </div>
      ) : null}

      {/* The lane strip shows the move cost; this is how it splits. */}
      <div className={cls.flex}>
        <Typography sx={sx.keyText}>Move cost at program rates</Typography>
        <Typography sx={sx.valText}>{getMonetaryValue(lines.moveCost)}</Typography>
      </div>
      {lines.showCoveredLine ? (
        <div className={cls.flex}>
          <Typography sx={sx.keyText}>Covered by the program</Typography>
          <Typography sx={sx.valText}>{getMonetaryValue(lines.covered)}</Typography>
        </div>
      ) : null}

      {lines.showReimbursementLine ? (
        <div className={cls.flex}>
          <Typography sx={sx.keyText}>Expected reimbursement from the program</Typography>
          <Typography sx={sx.valText}>{getMonetaryValue(lines.reimbursement)}</Typography>
        </div>
      ) : null}

      {lines.showRecoupLine ? (
        <div className={cls.flex}>
          <Typography sx={sx.keyText}>{RECOUP_LABEL}</Typography>
          <Typography sx={sx.valText}>{getMonetaryValue(lines.recoup)}</Typography>
        </div>
      ) : null}
      {lines.showYouPayLine ? (
        <div className={cls.flex}>
          <Typography sx={sx.keyText}>{YOUR_SHARE_LABEL}</Typography>
          <Typography sx={sx.valText}>{getMonetaryValue(lines.youPay)}</Typography>
        </div>
      ) : null}

      {eligibility ? (
        <>
          <Spacer />
          <Typography sx={{ ...sx.keyText, fontWeight: 600, mb: 0.5 }}>Eligibility</Typography>
          <EligibilityRuleRows view={eligibility} />
        </>
      ) : null}
    </>
  );
};

// STYLES ---------------------------------------------------------------- //

const useStyles = () => ({
  flex: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 4px;
  `,
});

const useSxStyles = (theme?: Theme) => ({
  keyText: {
    lineHeight: 1.5,
    fontSize: `14px`,
    color: theme?.palette?.text?.secondary,
  },
  valText: {
    lineHeight: 1.5,
    fontSize: `14px`,
    fontWeight: 600,
    color: theme?.palette?.text?.primary,
  },
});
